import {
  AuthorizedPermission,
  CreateRoutersProps,
  DOMRouterOpts,
  RoutePathItem,
  addPermissionByLoop,
  getBase,
} from '@maxtropy/components';
import scadaRoutes from './scada';
import mainRoutes from './main';
import homeRoutes from './home';
import { MainOutlet, ScadaOutlet } from './outlets';
import { createBrowserRouter, Outlet, useLocation, useNavigation } from 'react-router-dom';
import { useEffect } from 'react';
import ErrorBoundary from '@maxtropy/web-monitor/es/Boundary';
import HKPlayer from '@/pages/VideoMonitoring/components/HKVideoPlayer';
import IpConfiguration from '@/pages/IPConfiguration';
import ButtonConfig from '@/pages/IPConfiguration/components/ButtonConfig';
import PhoneConfig from '@/pages/IPConfiguration/components/PhoneConfig';

const dispatchMicroAppLoaded = () => {
  if (!window.__MICRO_APP_ENVIRONMENT__ || window.__PAGE_LOAD_DISPATCHED__) {
    return;
  }

  window.__PAGE_LOAD_DISPATCHED__ = true;
  window.rawWindow.dispatchEvent(
    new CustomEvent('micro-app-loaded', {
      detail: {
        name: 'ms-dc',
        data: { loaded: true },
      },
    })
  );
};

// 运行时错误桥接逻辑先临时关闭。
// 当前页面里存在部分三方脚本、HT/wasm、资源 fallback 等非业务致命错误，这些错误仍会在控制台暴露，
// 但不再主动抛给 React Router 的 errorElement，避免误跳统一 500 错误页。
// 如后续需要恢复运行时报错统一提示，可打开下面的 shouldIgnoreRuntimeError 和 RootWrapper 中的监听逻辑。
// const shouldIgnoreRuntimeError = (error: unknown, event?: ErrorEvent) => {
//   const message = error instanceof Error ? error.message : String(error ?? event?.message ?? '');
//   const stack = error instanceof Error ? error.stack ?? '' : '';
//   const source = `${event?.message ?? ''} ${message} ${event?.filename ?? ''} ${stack}`;
//
//   const isHtmlScriptFallbackError =
//     source.includes("Failed to set the 'textContent' property on 'HTMLScriptElement'") &&
//     (source.includes("Unexpected token '<'") ||
//       /font_[\w-]+.*\.js/.test(source) ||
//       source.includes('IconFont') ||
//       source.includes('runCode2InlineScript'));
//
//   const isHtRelatedError =
//     source.includes('chunk-ht-lib') || source.includes('chunk-ht-2d-client') || source.includes('pathkit');
//
//   const isHtWasmCompileError =
//     isHtRelatedError &&
//     source.includes('WebAssembly.instantiate()') &&
//     source.includes('CompileError') &&
//     source.includes('wasm');
//
//   const isHtRafTimerError =
//     isHtRelatedError &&
//     source.includes('RAF Timer callback error') &&
//     source.includes("Cannot read properties of undefined (reading 'position')");
//
//   return (
//     isHtmlScriptFallbackError ||
//     isHtWasmCompileError ||
//     isHtRafTimerError ||
//     source.includes('JSON.parse 失败')
//   );
// };

const RootWrapper = () => {
  const location = useLocation();
  const navigation = useNavigation();
  // const [runtimeError, setRuntimeError] = useState<Error | null>(null);

  // 在微前端环境下，路由跳转时触发popstate事件，使主应用监听到路由变化
  useEffect(() => {
    // 运行时错误桥接关闭后，不再在路由切换时重置 runtimeError。
    // setRuntimeError(null);
    if (!window.__MICRO_APP_ENVIRONMENT__) return;
    window.__PAGE_LOAD_DISPATCHED__ = false;
    window.rawWindow.dispatchEvent(new PopStateEvent('popstate', { state: null }));
  }, [location]);

  // 临时关闭运行时报错统一提示。
  // 这段监听会把 window/rawWindow 的 error 和 unhandledrejection 转成 React render 阶段异常，
  // 再交给 ErrorBoundary 展示 500 页面。现阶段部分三方资源错误不适合打断用户页面，所以先保留代码但不启用。
  // useEffect(() => {
  //   const errorTargets = [window, window.rawWindow].filter(Boolean);
  //
  //   const handleRuntimeError = (event: ErrorEvent) => {
  //     const error = event.error ?? new Error(event.message);
  //     if (shouldIgnoreRuntimeError(error, event)) return;
  //
  //     window.setTimeout(() => {
  //       setRuntimeError(error);
  //     });
  //   };
  //
  //   const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
  //     const reason = event.reason;
  //     if (shouldIgnoreRuntimeError(reason)) return;
  //
  //     window.setTimeout(() => {
  //       setRuntimeError(reason instanceof Error ? reason : new Error(String(reason ?? 'Unhandled promise rejection')));
  //     });
  //   };
  //
  //   errorTargets.forEach(target => {
  //     target.addEventListener('error', handleRuntimeError);
  //     target.addEventListener('unhandledrejection', handleUnhandledRejection);
  //   });
  //
  //   return () => {
  //     errorTargets.forEach(target => {
  //       target.removeEventListener('error', handleRuntimeError);
  //       target.removeEventListener('unhandledrejection', handleUnhandledRejection);
  //     });
  //   };
  // }, []);

  useEffect(() => {
    if (navigation.state === 'idle') {
      dispatchMicroAppLoaded();
    }
  }, [navigation.state]);

  // 运行时错误桥接关闭后，不再主动抛出 runtimeError 触发统一 500 页面。
  // if (runtimeError) {
  //   throw runtimeError;
  // }

  return <Outlet />;
};

const MsDcRouteErrorBoundary = () => {
  useEffect(() => {
    dispatchMicroAppLoaded();
  }, []);

  return <ErrorBoundary />;
};

const routes: RoutePathItem[] = [
  {
    errorElement: <MsDcRouteErrorBoundary />,
    element: <RootWrapper />,
    children: [
      {
        element: <ScadaOutlet />,
        children: [...scadaRoutes],
      },
      {
        element: <MainOutlet />,
        children: [...homeRoutes, ...mainRoutes],
      },
      {
        path: '/monitor/video/hkPlayer',
        element: <HKPlayer />,
      },
      {
        path: '/ip/configuration',
        element: <IpConfiguration />,
      },
      {
        path: '/ip/configuration/button-config',
        element: <ButtonConfig />,
      },
      {
        path: '/ip/configuration/phone-config',
        element: <PhoneConfig />,
      },
    ],
  },
];

export const createRouters = (parameters: CreateRoutersProps, opt?: DOMRouterOpts) => {
  const { routes, render } = parameters;
  const pagesRoutes = addPermissionByLoop({ routes, render });
  return createBrowserRouter(pagesRoutes, opt);
};

// 递归添加AuthorizedPermission,并创建路由对象
const routers = createRouters(
  {
    routes,
    render: (pers, ele, theme) => {
      return (
        <AuthorizedPermission theme={theme} permissionKey={pers}>
          {ele}
        </AuthorizedPermission>
      );
    },
  },
  { basename: getBase() }
);

export default routers;
